import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConexusSymbol from '@/components/logos/ConexusSymbol';
import heroStudents from '@/assets/hero-students.jpg';

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[88vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden bg-accent pt-24 md:pt-28 pb-16 md:pb-0">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#112E4A] via-[#112E4A] to-[#0A1F3A]" />

      {/* Decorative Stars — hide on mobile to reduce paint */}
      <div className="absolute inset-0 opacity-10 hidden md:block">
        <div className="absolute top-20 left-10 w-4 h-4 bg-primary rounded-full animate-pulse" />
        <div className="absolute top-40 right-20 w-3 h-3 bg-primary rounded-full animate-pulse delay-300" />
        <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-primary rounded-full animate-pulse delay-700" />
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-primary rounded-full animate-pulse delay-500" />
      </div>

      {/* Glowing Orbs — smaller & lighter on mobile */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 md:w-96 md:h-96 bg-primary/15 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />


      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* Animated Symbol */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="mb-5 md:mb-6"
              data-testid="hero-symbol"
            >
              <ConexusSymbol className="h-12 w-12 md:h-16 md:w-16 animate-float" />
            </motion.div>


            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 md:mb-6 leading-tight"
            >
              {t('hero.headline')}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base sm:text-lg md:text-xl text-white/90 mb-8 md:mb-10 max-w-xl leading-relaxed"
            >
              {t('hero.subheadline')}
            </motion.p>


            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                asChild
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-base md:text-lg px-6 md:px-8 w-full sm:w-auto"
              >
                <Link to="/contact">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/50 text-white hover:bg-white/10 text-base md:text-lg px-6 md:px-8 bg-white/10 w-full sm:w-auto"
              >
                <Link to="/programs">{t('hero.exploreProgramsCta')}</Link>
              </Button>
            </motion.div>


            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10 md:mt-12 grid grid-cols-3 gap-3 sm:gap-6 md:gap-8 max-w-md"
            >
              {[
                { value: '400+', label: t('hero.stats.students') },
                { value: '20+', label: t('hero.stats.universities') },
                { value: '99%', label: t('hero.stats.success') },
              ].map((stat, index) => (
                <div key={index} className="text-left">
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-[11px] sm:text-xs text-white/80 leading-tight">{stat.label}</div>
                </div>
              ))}
            </motion.div>

          </div>

          {/* Right Side - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-t from-[#112E4A] via-transparent to-transparent z-10 rounded-2xl" />
              <img
                src={heroStudents}
                alt="Happy international student celebrating success"
                className="rounded-2xl shadow-2xl object-cover w-full h-64 sm:h-80 md:h-96 lg:h-[500px]"
                loading="eager"
              />
            </div>
          </motion.div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-primary rounded-full" />
        </motion.div>
      </motion.div>

    </section>
  );
};

export default HeroSection;
